import { useCallback, useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';

import cn from 'classnames';
import { Button, LoaderSpinner } from 'components/UtilElements';
import {
  AuthAction,
  AuthActions,
  AuthorizationResponse,
  GetSsoStatusResponse,
} from 'containers/AuthStateManager';
import { AuthScreens } from 'globals/constants';
import { useInterval } from 'hooks/use-interval';
import { Dispatch } from 'redux';
import { SET_AUTH_STATUS } from 'redux/actions';
import { API, request } from 'redux/api';
import { RootState } from 'redux/reducers';
import { setContextCourseId, setContextUserId, setSessionToken } from 'utils/session';

interface SsoLoginProps {
  accessData: SessionData | null;
  setAuthState(action: AuthAction): void;
  setAuthorization(isAuthorized: boolean): void;
}

const getSsoStatus = async (sessionId: string) => {
  const response = await request<GetSsoStatusResponse>({
    method: 'GET',
    url: API.GET_SSO_STATUS(sessionId),
    baseURL: process.env.REACT_APP_SSO_GATEWAY_URL,
  });
  return response;
};

const ssoLogin = async (authorizationToken: string, sessionId: string) => {
  const response = await request<AuthorizationResponse>({
    method: 'POST',
    url: API.SSO_LOGIN,
    headers: {
      Authorization: `Bearer ${authorizationToken}`,
    },
    data: { sessionId, agent: 'web' },
  });
  return response;
};

const SsoLogin = ({ accessData, setAuthorization, setAuthState }: SsoLoginProps) => {
  const ssoAuthTabRef = useRef<Window | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const sessionToken = accessData?.sessionToken;
  const ssoSessionId = accessData?.ssoSessionId;

  const { start, stop } = useInterval(async () => {
    if (!ssoAuthTabRef.current || ssoAuthTabRef.current.closed) {
      setErrorMessage(
        'The SSO login process could not be completed. Please try again. If the issue persists, please write to support@acadly.com'
      );
      stop();
      return;
    }

    if (!ssoSessionId || !sessionToken) {
      stopSso();
      return;
    }

    try {
      const response = await getSsoStatus(ssoSessionId);
      switch (response.message) {
        case 'success': {
          const response = await ssoLogin(sessionToken, ssoSessionId);
          setSessionToken(response.authToken);
          if (response.hasLmsApiToken) {
            setAuthorization(true);
            setContextCourseId(accessData?.courseDetails.courseId);
            setContextUserId(accessData?.userId);
          } else {
            setAuthState({
              type: AuthActions.AUTHORIZE_ACADLY,
              payload: response,
            });
          }
          stopSso();
          break;
        }
        case 'failed':
          {
            const { errorMessage } = response;
            setErrorMessage(
              errorMessage ||
                'The SSO login process could not be completed. Please try again. If the issue persists, please write to support@acadly.com'
            );
            stopSso();
          }
          break;
        case 'waiting':
        default:
          // do nothing, just wait for next call
          break;
      }
    } catch (error: any) {
      console.log(error);
      setErrorMessage(error.message);
      stop();
    }
  }, null);

  const stopSso = useCallback(() => {
    stop();
    ssoAuthTabRef.current?.close();
    ssoAuthTabRef.current = null;
  }, [stop]);

  const onSsoInit = useCallback(() => {
    if (!accessData?.ssoAuthUrl) return;
    const ssoAuthTab = window.open(accessData.ssoAuthUrl, '_blank');
    if (!ssoAuthTab) {
      setErrorMessage(
        'It seems like your browser is blocking popups. Please allow popups for this site and try again.'
      );
      return;
    }
    ssoAuthTab.focus();
    ssoAuthTabRef.current = ssoAuthTab;
    start(5000);
  }, [accessData?.ssoAuthUrl, start]);

  const handleLoginAgain = () => {
    setAuthState({
      type: AuthActions.SSO_LOGIN,
      payload: AuthScreens.SSO_AUTHENTICATION,
    });
  };

  useEffect(() => {
    onSsoInit();
  }, [onSsoInit]);

  if (errorMessage) {
    return (
      <>
        <div className='auth__panel'>
          <p className={cn('auth__error', { shake: !!errorMessage })}>{errorMessage}</p>
          <div className={cn('auth__action-container')}>
            <Button
              formButton
              type='ghost'
              selector={cn('btn-primary', 'auth__action')}
              onRipple={handleLoginAgain}
            >
              Log in again
            </Button>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <h2 className='auth__title'>SSO Login</h2>
      <div className='auth__panel'>
        <LoaderSpinner />
        <p>
          Please complete the login process in the newly opened window.
          <br />
          This page will automatically update once you've finished
        </p>
      </div>
    </>
  );
};
const mapStateToProps = ({ getAccess }: RootState) => {
  return { accessData: getAccess };
};

const mapDispatchToProps = (dispatch: Dispatch) => ({
  setAuthorization: (isAuthorized: boolean) => {
    dispatch({
      type: SET_AUTH_STATUS,
      payload: isAuthorized,
    });
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(SsoLogin);
